<script lang="ts">
  import { frameAt, MARK_EVERY_FRAME_UPTO, tickMarks } from '../map/layers/ticks'
  import { layers, setIndex, setPlaying, togglePlaying } from '../state/layers.svelte'
  import { layout } from '../state/layout.svelte'
  import Icon from './Icon.svelte'
  import { haptic } from '../native'

  /**
   * The shared time axis: play/pause, a scrubber, and the label for the frame
   * showing now.
   *
   * It sits over the map, so `touch-action: none` and a `pointerdown` that
   * stops propagating are what keep a horizontal drag on the track from panning
   * the map underneath. `lib/sheet.ts` handles the same trap vertically.
   */

  let count = $derived(layers.times.length)
  let current = $derived(layers.times[Math.min(layers.index, count - 1)] ?? null)

  const label = new Intl.DateTimeFormat(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  })
  const dayLabel = new Intl.DateTimeFormat(undefined, { weekday: 'short' })

  function frameLabel(d: Date | null): string {
    if (!d) return ''
    const sameDay = new Date().toDateString() === d.toDateString()
    return sameDay ? label.format(d) : `${dayLabel.format(d)} ${label.format(d)}`
  }

  /**
   * The longest labels this axis can show, which is what the readout is
   * sized to. Sized to the label on screen, it grew by a weekday the moment
   * the thumb crossed midnight ("9:00 PM" to "Tue 1:00 AM") and took that
   * width from the track, which then shrank under the finger mid-scrub.
   *
   * Every label of the greatest length is kept rather than one, because
   * "Wed" is wider than "Tue" in a proportional face at the same length; the
   * grid cell takes the widest of them. Figures are tabular, so digits never
   * decide it.
   */
  let widest = $derived.by(() => {
    const labels = [...new Set(layers.times.map(frameLabel))]
    const longest = Math.max(0, ...labels.map((l) => l.length))
    return labels.filter((l) => l.length === longest)
  })

  function onInput(e: Event & { currentTarget: HTMLInputElement }) {
    setIndex(Number(e.currentTarget.value))
  }

  /**
   * Arrow keys step, Home and End jump to the ends.
   *
   * A native `<input type="range">` already does the arrows, and it does them
   * *while playing* — which fights the loop, because the next tick moves the
   * thumb back. So a key press pauses first: scrubbing by hand and a running
   * loop are two different intentions and the keyboard should mean the first.
   */
  function onKeydown(e: KeyboardEvent) {
    const last = count - 1
    const keys: Record<string, number> = {
      ArrowLeft: layers.index - 1,
      ArrowDown: layers.index - 1,
      ArrowRight: layers.index + 1,
      ArrowUp: layers.index + 1,
      Home: 0,
      End: last,
    }
    const next = keys[e.key]
    if (next === undefined) return
    e.preventDefault()
    if (layers.playing) setPlaying(false)
    setIndex(Math.max(0, Math.min(last, next)))
  }

  /**
   * Where the marks go. `tickMarks` owns the rule; see `map/layers/ticks.ts`.
   *
   * Below twenty frames every frame is marked, as before. Above it the marks
   * become a time *scale* rather than a frame count, because a 73-step
   * sub-hourly run is now an ordinary axis and per-frame marks at that density
   * are a grey bar. Positions are frame indices either way: the track is
   * index-spaced, which is the honest rendering of a non-uniform axis.
   *
   * How many marks is a layout question, so it is answered here: the desktop
   * track is ~740 px and a phone's is ~130 px once the transport cluster and
   * the readout have taken their share.
   */
  let ticks = $derived(tickMarks(layers.times, layout.isDesktop ? 24 : 8))

  let index = $derived(Math.min(layers.index, Math.max(0, count - 1)))
  let cursorLeft = $derived(count > 1 ? (index / (count - 1)) * 100 : 0)

  /**
   * Where the radar ends and the forecast begins, when a layer's frames run
   * past now (the radar map's next hour). The track is dashed after it, a
   * mark stands on it, and the readout says which half the frame is in and
   * how far ahead: a forecast frame must never pass for an observation.
   */
  let nowIndex = $derived(
    layers.nowIndex !== null && layers.nowIndex < count ? layers.nowIndex : null,
  )
  let nowLeft = $derived(nowIndex !== null && count > 1 ? (nowIndex / (count - 1)) * 100 : null)
  let ahead = $derived.by(() => {
    if (nowIndex === null || index <= nowIndex || !current) return null
    const from = layers.times[nowIndex]
    return Math.round((current.getTime() - from.getTime()) / 60_000)
  })
  let caption = $derived(
    nowIndex === null ? null : ahead === null ? 'Radar' : `Forecast +${ahead} min`,
  )
  /** The widest caption the axis can show, so the readout never changes width. */
  let captionSizer = $derived.by(() => {
    if (nowIndex === null) return null
    const last = layers.times[count - 1]
    const minutes = Math.round((last.getTime() - layers.times[nowIndex].getTime()) / 60_000)
    return `Forecast +${minutes} min`
  })
  let valueText = $derived(
    ahead === null
      ? frameLabel(current)
      : `${frameLabel(current)}, forecast ${ahead} minutes ahead`,
  )

  /**
   * Step buttons, and only when the track is too dense to aim at.
   *
   * At twelve frames the thumb covers a twelfth of the track and dragging it
   * one stop is easy. At seventy-three it is four pixels, which no thumb finds
   * on a touch screen — so the frame-by-frame control has to exist as a target
   * of its own rather than as a drag nobody can land. Below the threshold they
   * would be two buttons stealing width from the track for nothing.
   */
  let showSteps = $derived(count > MARK_EVERY_FRAME_UPTO)

  function step(delta: number) {
    if (layers.playing) setPlaying(false)
    const next = Math.max(0, Math.min(count - 1, index + delta))
    if (next !== index) haptic('selection')
    setIndex(next)
  }

  /**
   * A finger on the track scrubs to the frame under it, wherever it lands.
   *
   * iOS moves a native range only from its thumb, so on a coarse pointer the
   * input takes no pointer events at all (the CSS below) and this does the
   * whole gesture; the input stays for the keyboard, VoiceOver and the name.
   * A mouse or a pen is left to the input, which already seeks on a click.
   */
  let trackEl = $state<HTMLInputElement | null>(null)
  let scrubbing = false // Not $state: only these handlers read it.

  function scrubTo(e: PointerEvent) {
    if (!trackEl) return
    const rect = trackEl.getBoundingClientRect()
    const thumb = parseFloat(getComputedStyle(trackEl).getPropertyValue('--thumb')) || 14
    const next = frameAt(e.clientX - rect.left, rect.width, thumb, count)
    if (next !== index) setIndex(next)
  }

  function onScrubStart(e: PointerEvent & { currentTarget: HTMLElement }) {
    if (e.pointerType !== 'touch') return
    scrubbing = true
    e.currentTarget.setPointerCapture?.(e.pointerId)
    if (layers.playing) setPlaying(false)
    scrubTo(e)
  }

  function onScrubMove(e: PointerEvent) {
    if (scrubbing) scrubTo(e)
  }

  function onScrubEnd() {
    scrubbing = false
  }
</script>

{#if count > 1}
  <div
    class="slider-plate"
    onpointerdown={(e) => e.stopPropagation()}
    role="group"
    aria-label="Overlay time"
  >
    <!-- One transport cluster rather than buttons either side of the track:
         the track stays contiguous, and on a phone the three 44 px targets sit
         under the same thumb instead of at opposite ends of the plate. -->
    <div class="transport">
      {#if showSteps}
        <button
          type="button"
          class="step touch-target"
          aria-label="Previous frame"
          disabled={index === 0}
          onclick={() => step(-1)}
        >
          <Icon name="chevron-left" />
        </button>
      {/if}

      <button
        type="button"
        class="play touch-target"
        aria-label={layers.playing ? 'Pause the loop' : 'Play the loop'}
        aria-pressed={layers.playing}
        onclick={togglePlaying}
      >
        <Icon name={layers.playing ? 'pause' : 'play'} size={16} strokeWidth={0} filled />
      </button>

      {#if showSteps}
        <button
          type="button"
          class="step touch-target"
          aria-label="Next frame"
          disabled={index === count - 1}
          onclick={() => step(1)}
        >
          <Icon name="chevron-right" />
        </button>
      {/if}
    </div>

    <div class="track-wrap">
      <!-- svelte-ignore a11y_no_static_element_interactions (the input inside
           is the control and carries the role; this only widens its touch) -->
      <div
        class="track-stack"
        onpointerdown={onScrubStart}
        onpointermove={onScrubMove}
        onpointerup={onScrubEnd}
        onpointercancel={onScrubEnd}
      >
        <div class="marks" aria-hidden="true">
          {#each ticks as tick (tick.index)}
            <span class="mark" class:day={tick.day} style="left: {tick.left}%"></span>
          {/each}
          <!-- The frame the thumb is on, always drawn: with a time scale
               rather than a mark per frame, the current frame is usually not
               one of the marks. -->
          {#if nowLeft !== null}
            <!-- Now: the newest radar frame, after which the frames are the
                 radar carried forward. -->
            <span class="mark now" style="left: {nowLeft}%"></span>
          {/if}
          <span class="mark on" style="left: {cursorLeft}%"></span>
        </div>
        <input
          bind:this={trackEl}
          class="track"
          type="range"
          min="0"
          max={count - 1}
          step="1"
          value={index}
          style:--p={count > 1 ? index / (count - 1) : 0}
          style:--now-p={nowLeft === null ? 1 : nowLeft / 100}
          class:forecast-axis={nowLeft !== null}
          aria-label="Frame"
          aria-valuetext={valueText}
          oninput={onInput}
          onkeydown={onKeydown}
        />
      </div>
      <div class="ticks" aria-hidden="true">
        <span>{frameLabel(layers.times[0])}</span>
        <span>{frameLabel(layers.times[count - 1])}</span>
      </div>
    </div>

    <!-- One width for the whole axis: the label shown and, invisibly in the
         same grid cell, the longest labels the axis can show. -->
    <div class="readout">
      {#if caption !== null}
        <div class="caption" class:ahead={ahead !== null}>
          <span>{caption}</span>
          <span class="now-sizer" aria-hidden="true">{captionSizer}</span>
        </div>
      {/if}
      <div class="now">
        <span class="now-label" aria-live={layout.reducedMotion ? 'polite' : 'off'}
          >{frameLabel(current)}</span
        >
        {#each widest as text (text)}
          <span class="now-sizer" aria-hidden="true">{text}</span>
        {/each}
      </div>
    </div>

    {#if layers.loading}<span class="loading muted">loading…</span>{/if}
  </div>
{/if}

<style>
  /* The bottom row of the map's card (Phase 10 plan §6): `MapView` places the
     card, and the legend's strip sits above this row with a 2 px rule
     between them. With no legend this row is the whole card. */
  .slider-plate {
    display: flex;
    align-items: center;
    gap: 16px;
    height: 56px;
    padding: 0 16px 0 8px;
    /* The whole row, not just the thumb: a drag anywhere on it is a scrub,
       never a map pan. */
    touch-action: none;
  }

  .slider-plate:not(:first-child) {
    border-top: 2px solid var(--color-divider);
  }

  .transport {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    gap: 2px;
  }

  /* 1B's play: a 40 px accent square with the glyph in the ground. The glyph
     is a mark, not text, so it needs 3:1 and the accent/ground pair is
     measured at that in tokens.test.ts. */
  .play {
    flex: 0 0 auto;
    width: 40px;
    height: 40px;
    padding: 0;
    border: 0;
    background: var(--color-accent);
    color: var(--color-bg);
    cursor: pointer;
    display: grid;
    place-items: center;
  }

  /* Narrower than play on a fine pointer, where the cursor lands wherever it
     is pointed. `touch-target` takes them to 44 px on a coarse one, which is
     what the transport cluster is sized around. */
  .step {
    flex: 0 0 auto;
    width: 28px;
    height: 40px;
    padding: 0;
    border: 0;
    background: none;
    color: var(--color-text);
    cursor: pointer;
    display: grid;
    place-items: center;
  }

  .step:not(:disabled):hover {
    background: color-mix(in srgb, var(--color-text) 7%, transparent);
  }

  .step:disabled {
    opacity: 0.35;
    cursor: default;
  }

  .track-wrap {
    flex: 1 1 auto;
    min-width: 0;
    align-self: stretch;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  /* The thumb's box. The square drawn in it is 14 px either way; on a coarse
     pointer the box is 32 px of transparent margin around it, so a finger
     has something to find. `--thumb` is also what insets the marks and what
     `frameAt` reads back, so the three cannot disagree. */
  .track-stack {
    position: relative;
    --thumb: 14px;
  }

  @media (pointer: coarse) {
    .track-stack {
      --thumb: 32px;
    }
  }

  /* 1B's tick grid, standing on the track. Inset by half a thumb at each end,
     because that is how far in the thumb's centre stops, so a mark sits
     exactly where the thumb will land. `pointer-events: none` keeps the whole
     row draggable through them. */
  .marks {
    position: absolute;
    left: calc(var(--thumb) / 2);
    right: calc(var(--thumb) / 2);
    bottom: calc(50% + 2px);
    height: 10px;
    pointer-events: none;
    z-index: 2;
  }

  .mark {
    position: absolute;
    bottom: 0;
    width: 1px;
    height: 10px;
    margin-left: -0.5px;
    background: var(--color-divider);
  }

  /* A frame that starts a local day. The one scale mark worth telling apart
     without a label: on a 48-hour axis it is where "tomorrow" begins, which is
     what somebody scrubbing a forecast is looking for. */
  .mark.day {
    width: 2px;
    height: 14px;
    margin-left: -1px;
    background: var(--color-text);
  }

  /* The frame the thumb is on. It stands above the thumb as a pointer, and
     is drawn last so it wins wherever it coincides with a scale mark. */
  .mark.on {
    width: 2px;
    margin-left: -1px;
    background: var(--color-accent);
  }

  /* Now stands taller than a day mark and reaches down across the track, so
     it reads as a divide rather than as one more tick. */
  .mark.now {
    width: 2px;
    height: 18px;
    margin-left: -1px;
    bottom: -8px;
    background: var(--color-text);
  }

  /* The native range, redrawn: a 4 px track, accent up to the thumb and the
     rule after it (1B draws the whole track accent and no thumb; the thumb
     stays, §6), and the thumb as a 14 px accent square in a 2 px ground
     border (1A's). `--p` is the frame's place on the axis, 0 to 1, and the
     fill ends at the thumb's centre, which is half a thumb in at either end. */
  .track {
    --fill: calc(var(--thumb) / 2 + (100% - var(--thumb)) * var(--p, 0));
    --thumb-paint:
      linear-gradient(var(--color-accent), var(--color-accent)) center / 10px 10px no-repeat,
      linear-gradient(var(--color-bg), var(--color-bg)) center / 14px 14px no-repeat;

    display: block;
    width: 100%;
    height: 28px;
    margin: 0;
    padding: 0;
    background: transparent;
    -webkit-appearance: none;
    appearance: none;
    cursor: pointer;
    touch-action: none;
  }

  .track::-webkit-slider-runnable-track {
    height: 4px;
    background: linear-gradient(
      to right,
      var(--color-accent) var(--fill),
      var(--color-divider) var(--fill)
    );
  }

  .track::-moz-range-track {
    height: 4px;
    background: linear-gradient(
      to right,
      var(--color-accent) var(--fill),
      var(--color-divider) var(--fill)
    );
  }

  /* Past now the unfilled track is dashed: the frames there are the radar
     carried forward, not the radar. `--now` is where the thumb's centre
     stands on the last observed frame, the same arithmetic as `--fill`. */
  .track.forecast-axis {
    --now: calc(var(--thumb) / 2 + (100% - var(--thumb)) * var(--now-p, 1));
    --dashes:
      linear-gradient(to right, var(--color-accent) var(--fill), transparent var(--fill)),
      linear-gradient(to right, var(--color-divider) var(--now), transparent var(--now)),
      repeating-linear-gradient(to right, var(--color-divider) 0 4px, transparent 4px 7px);
  }

  .track.forecast-axis::-webkit-slider-runnable-track {
    background: var(--dashes);
  }

  .track.forecast-axis::-moz-range-track {
    background: var(--dashes);
  }

  .track::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: var(--thumb);
    height: var(--thumb);
    /* Centred on the 4 px track. */
    margin-top: calc(2px - var(--thumb) / 2);
    border: 0;
    border-radius: 0;
    /* WebKit's own thumb draws a white box with a shadow unless told not to,
       which on a coarse pointer is the whole 32 px touch box. */
    box-shadow: none;
    background: var(--thumb-paint);
  }

  .track::-moz-range-thumb {
    width: var(--thumb);
    height: var(--thumb);
    border: 0;
    border-radius: 0;
    background: var(--thumb-paint);
  }

  .track:focus-visible {
    outline: none;
  }

  .track:focus-visible::-webkit-slider-thumb {
    outline: 2px solid var(--color-accent);
    outline-offset: 1px;
  }

  .track:focus-visible::-moz-range-thumb {
    outline: 2px solid var(--color-accent);
    outline-offset: 1px;
  }

  /* On a finger the gesture is `onScrubStart`'s, from wherever it lands; the
     input keeps the keyboard, the screen reader and the name. 44 px tall so
     the row is a target without being a taller bar. */
  @media (pointer: coarse) {
    .track {
      height: 44px;
      pointer-events: none;
    }
  }

  .ticks {
    display: flex;
    white-space: nowrap;
    justify-content: space-between;
    font-size: 10px;
    color: var(--color-muted);
    font-variant-numeric: tabular-nums;
    margin-top: -6px;
  }

  .readout {
    flex: 0 0 auto;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
  }

  /* Which half of the axis the frame is in. Muted on the radar, ink on a
     forecast: the one word that must not be missed. */
  .caption {
    display: grid;
    font-size: 10px;
    font-weight: 600;
    line-height: 1.2;
    color: var(--color-muted);
    text-transform: uppercase;
    letter-spacing: 0.04em;
    white-space: nowrap;
    text-align: right;
  }

  .caption > * {
    grid-area: 1 / 1;
  }

  .caption.ahead {
    color: var(--color-text);
  }

  .now {
    flex: 0 0 auto;
    display: grid;
    font-variant-numeric: tabular-nums;
    font-size: 15px;
    font-weight: 800;
    white-space: nowrap;
    text-align: right;
  }

  .now > * {
    grid-area: 1 / 1;
  }

  .now-sizer {
    visibility: hidden;
  }

  .loading {
    font-size: 11px;
    flex: 0 0 auto;
  }

  @media (max-width: 899px) {
    .slider-plate {
      gap: 12px;
      padding: 0 14px 0 6px;
    }

    .play {
      width: 44px;
      height: 44px;
    }

    /* 1B draws no end labels on the phone, and with the step buttons out the
       track is ~200 px, where "Tue 7:15 PM" and "Wed 7:00 AM" wrap into each
       other. The readout names the frame shown; the ends are a scrub away. */
    .ticks {
      display: none;
    }

    .now {
      font-size: 14px;
    }
  }
</style>
